// src/components/login/LoginForm.js
import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { auth, database } from '../../firebase';

const LoginForm = ({ onSwitchMode, setError }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  
  // Check if we should redirect after login
  useEffect(() => {
    // Check for redirect in URL params
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');
    
    if (redirectParam) {
      setRedirectAfterLogin(redirectParam);
    }
    
    // Also check for a pending booking in localStorage
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const bookingData = JSON.parse(pendingBooking);
        if (bookingData.returnPath) {
          setRedirectAfterLogin('booking');
        }
      } catch (e) {
        console.error('Error parsing pending booking data:', e);
      }
    }
  }, [location]);
  
  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('Attempting login with:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful:', userCredential.user.uid);
      
      // Update last login time
      try {
        await set(ref(database, `users/${userCredential.user.uid}/lastLogin`), new Date().toISOString());
        console.log('Updated last login time');
      } catch (dbError) {
        console.error('Error updating last login time:', dbError);
        // Continue with navigation even if updating the timestamp fails
      }
      
      // Handle redirection based on pending booking
      if (redirectAfterLogin === 'booking') {
        // Get the detailed booking information
        const pendingBooking = localStorage.getItem('pendingBooking');
        
        if (pendingBooking) {
          try {
            // Parse the booking data
            const bookingData = JSON.parse(pendingBooking);
            
            // Mark that authentication is completed
            bookingData.userAuthCompleted = true;
            
            // Store updated booking data back to localStorage
            localStorage.setItem('pendingBooking', JSON.stringify(bookingData));
            
            // Navigate to the exact return path that was stored
            if (bookingData.returnPath) {
              console.log('Redirecting to:', bookingData.returnPath);
              navigate(bookingData.returnPath);
            } else {
              // Fallback to standard booking route
              navigate('/booking');
            }
          } catch (e) {
            console.error('Error processing pending booking:', e);
            navigate('/booking'); // Fallback
          }
        } else {
          navigate('/booking');
        }
      } else {
        navigate('/account');
      }
    } catch (error) {
      console.error('Error during login:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Show the actual error message if we don't have a specific handler
      if (getErrorMessage(error.code) === 'An error occurred. Please try again') {
        setError(`Firebase error: ${error.message}`);
      } else {
        setError(getErrorMessage(error.code));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Convert Firebase error codes to user-friendly messages
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/invalid-credential':
        return 'Invalid login credentials';
      case 'auth/operation-not-allowed':
        return 'This login method is not enabled';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please try again later';
      case 'auth/missing-password':
        return 'Please enter a password';
      case 'auth/internal-error':
        return 'An internal Firebase error occurred. Please try again';
      case 'auth/configuration-not-found':
        return 'Firebase authentication not properly configured. Please contact support.';
      default:
        return 'An error occurred. Please try again';
    }
  };
  
  return (
    <form onSubmit={handleLogin}>
      <div className="mb-4">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email address
        </label>
        <div className="relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
            placeholder="you@example.com"
          />
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password
        </label>
        <div className="relative rounded-md shadow-sm">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="appearance-none block w-full pl-10 pr-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
            placeholder="Your password"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <input
            id="remember-me"
            name="remember-me"
            type="checkbox"
            className="h-4 w-4 text-[#3A98B9] focus:ring-[#3A98B9] border-gray-300 rounded"
          />
          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
            Remember me
          </label>
        </div>

        <div className="text-sm">
          <Link to="/forgot-password" className="font-medium text-[#3A98B9] hover:text-[#2A87A8]">
            Forgot password?
          </Link>
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={loading}
          className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#3A98B9] hover:bg-[#2A87A8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A98B9] ${
            loading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            <>
              <span>Sign in</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </button>
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?
          {' '}
          <button
            type="button"
            onClick={onSwitchMode}
            className="font-medium text-[#3A98B9] hover:text-[#2A87A8]"
          >
            Sign up now
          </button>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;